import React, { useState, useEffect, useCallback } from 'react';
import { structureAPI, teamDirectoryAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { ScrollArea, ScrollBar } from '../../components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../../components/ui/sheet';
import { Badge } from '../../components/ui/badge';
import { 
  ZoomIn, ZoomOut, Maximize2, User, Globe, Building2, 
  MapPin, Mail, Phone, Twitter, Linkedin, 
  Instagram, Briefcase, Users, Settings2, Truck,
  Cog, Star, Package, Home
} from 'lucide-react';

// Operations Department Color Scheme
const OPS_STYLES = {
  national: {
    coo: { bg: '#1E40AF', text: '#FFFFFF', border: '#1E3A8A' },
    director: { bg: '#3B82F6', text: '#FFFFFF', border: '#2563EB' },
    subDirector: { bg: '#60A5FA', text: '#FFFFFF', border: '#3B82F6' }
  },
  regional: {
    manager: { bg: '#059669', text: '#FFFFFF', border: '#047857' },
    deptManager: { bg: '#10B981', text: '#FFFFFF', border: '#059669' },
    lead: { bg: '#34D399', text: '#1F2937', border: '#10B981' },
    staff: { bg: '#A7F3D0', text: '#1F2937', border: '#6EE7B7' }
  },
  uniqueIP: {
    director: { bg: '#DC2626', text: '#FFFFFF', border: '#B91C1C' },
    subDirector: { bg: '#EF4444', text: '#FFFFFF', border: '#DC2626' },
    head: { bg: '#F87171', text: '#1F2937', border: '#EF4444' },
    lead: { bg: '#FCA5A5', text: '#1F2937', border: '#F87171' },
    staff: { bg: '#FEE2E2', text: '#1F2937', border: '#FECACA' }
  },
  vacant: { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A' }
};

// Demo Operations Hierarchy Data
const DEMO_OPS_HIERARCHY = {
  national: {
    coo: {
      id: 'coo-ops',
      title: 'Chief Operating Officer',
      assignedPersonId: 'p-coo',
      assignedPerson: { displayName: 'Michael Thompson', email: 'michael.thompson@cognitive.com' },
      scopeType: 'national',
      levelLabel: 'C-Suite'
    },
    director: {
      id: 'dir-ops',
      title: 'Director of Operations',
      assignedPersonId: 'p-dir-ops',
      assignedPerson: { displayName: 'Jennifer Martinez', email: 'jennifer.martinez@cognitive.com' },
      scopeType: 'national',
      levelLabel: 'Director'
    },
    subDirectors: [
      { id: 'sd-prod', title: 'Director of Production', assignedPersonId: 'p-sd-prod', assignedPerson: { displayName: 'David Lee' }, scopeType: 'national', department: 'Production' },
      { id: 'sd-exp', title: 'Director of Experience', assignedPersonId: 'p-sd-exp', assignedPerson: { displayName: 'Rachel Green' }, scopeType: 'national', department: 'Experience' },
      { id: 'sd-hosp', title: 'Director of Hospitality', assignedPerson: null, scopeType: 'national', department: 'Hospitality' },
      { id: 'sd-admin', title: 'Director of Administration', assignedPersonId: 'p-sd-admin', assignedPerson: { displayName: 'Robert Wilson' }, scopeType: 'national', department: 'Administration' }
    ],
    regions: {
      '1': {
        manager: {
          id: 'rom-1',
          title: 'Operations & Logistics Manager',
          assignedPersonId: 'p-rom1',
          assignedPerson: { displayName: 'Sarah Johnson', email: 'sarah.j@cognitive.com' },
          scopeType: 'region_1',
          levelLabel: 'Manager'
        },
        departments: {
          production: {
            manager: { id: 'r1-pm', title: 'Production Manager', assignedPersonId: 'p-r1pm', assignedPerson: { displayName: 'Tom Harris' }, scopeType: 'region_1' },
            leads: [
              { id: 'r1-pl1', title: 'Production Lead', assignedPersonId: 'p-r1pl1', assignedPerson: { displayName: 'Alex Chen' }, scopeType: 'region_1' },
              { id: 'r1-pl2', title: 'Stage Lead', assignedPersonId: 'p-r1pl2', assignedPerson: { displayName: 'Jamie Wilson' }, scopeType: 'region_1' }
            ],
            staff: [
              { id: 'r1-ps1', title: 'Production Coordinator', assignedPersonId: 'p-r1ps1', assignedPerson: { displayName: 'Casey Brown' }, scopeType: 'region_1' },
              { id: 'r1-ps2', title: 'Technical Staff', assignedPersonId: 'p-r1ps2', assignedPerson: { displayName: 'Jordan Lee' }, scopeType: 'region_1' }
            ]
          },
          experience: {
            manager: { id: 'r1-em', title: 'Experience Manager', assignedPersonId: 'p-r1em', assignedPerson: { displayName: 'Morgan Davis' }, scopeType: 'region_1' },
            leads: [
              { id: 'r1-el1', title: 'Guest Services Lead', assignedPersonId: 'p-r1el1', assignedPerson: { displayName: 'Riley Park' }, scopeType: 'region_1' }
            ],
            staff: [
              { id: 'r1-es1', title: 'Experience Coordinator', assignedPersonId: 'p-r1es1', assignedPerson: { displayName: 'Taylor Kim' }, scopeType: 'region_1' },
              { id: 'r1-es2', title: 'Guest Services Staff', assignedPersonId: 'p-r1es2', assignedPerson: { displayName: 'Sam Rodriguez' }, scopeType: 'region_1' }
            ]
          }
        }
      },
      '2': {
        manager: {
          id: 'rom-2',
          title: 'Operations & Logistics Manager',
          assignedPersonId: 'p-rom2',
          assignedPerson: { displayName: 'David Kim', email: 'david.kim@cognitive.com' },
          scopeType: 'region_2',
          levelLabel: 'Manager'
        },
        departments: {
          production: {
            manager: { id: 'r2-pm', title: 'Production Manager', assignedPersonId: 'p-r2pm', assignedPerson: { displayName: 'Lisa Wang' }, scopeType: 'region_2' },
            leads: [
              { id: 'r2-pl1', title: 'Production Lead', assignedPerson: null, scopeType: 'region_2' },
              { id: 'r2-pl2', title: 'Logistics Lead', assignedPersonId: 'p-r2pl2', assignedPerson: { displayName: 'Chris Evans' }, scopeType: 'region_2' }
            ],
            staff: [
              { id: 'r2-ps1', title: 'Production Staff', assignedPersonId: 'p-r2ps1', assignedPerson: { displayName: 'Emma Stone' }, scopeType: 'region_2' }
            ]
          },
          experience: {
            manager: { id: 'r2-em', title: 'Experience Manager', assignedPerson: null, scopeType: 'region_2' },
            leads: [
              { id: 'r2-el1', title: 'Experience Lead', assignedPersonId: 'p-r2el1', assignedPerson: { displayName: 'Nina Patel' }, scopeType: 'region_2' }
            ],
            staff: [
              { id: 'r2-es1', title: 'Experience Coordinator', assignedPersonId: 'p-r2es1', assignedPerson: { displayName: 'Mike Chen' }, scopeType: 'region_2' }
            ]
          }
        }
      },
      '3': {
        manager: {
          id: 'rom-3',
          title: 'Operations & Logistics Manager',
          assignedPerson: null,
          scopeType: 'region_3',
          levelLabel: 'Manager'
        },
        departments: {
          production: {
            manager: { id: 'r3-pm', title: 'Production Manager', assignedPersonId: 'p-r3pm', assignedPerson: { displayName: 'James Brown' }, scopeType: 'region_3' },
            leads: [
              { id: 'r3-pl1', title: 'Production Lead', assignedPersonId: 'p-r3pl1', assignedPerson: { displayName: 'Anna White' }, scopeType: 'region_3' }
            ],
            staff: [
              { id: 'r3-ps1', title: 'Production Staff', assignedPersonId: 'p-r3ps1', assignedPerson: { displayName: 'Ryan Murphy' }, scopeType: 'region_3' }
            ]
          },
          experience: {
            manager: { id: 'r3-em', title: 'Experience Manager', assignedPersonId: 'p-r3em', assignedPerson: { displayName: 'Sophie Turner' }, scopeType: 'region_3' },
            leads: [
              { id: 'r3-el1', title: 'Site Manager', assignedPersonId: 'p-r3el1', assignedPerson: { displayName: 'Kevin Hart' }, scopeType: 'region_3' }
            ],
            staff: []
          }
        }
      },
      '4': {
        manager: {
          id: 'rom-4',
          title: 'Operations & Logistics Manager',
          assignedPersonId: 'p-rom4',
          assignedPerson: { displayName: 'Jennifer Lee', email: 'jennifer.lee@cognitive.com' },
          scopeType: 'region_4',
          levelLabel: 'Manager'
        },
        departments: {
          production: {
            manager: { id: 'r4-pm', title: 'Production Manager', assignedPersonId: 'p-r4pm', assignedPerson: { displayName: 'Brandon Cole' }, scopeType: 'region_4' },
            leads: [
              { id: 'r4-pl1', title: 'Production Lead', assignedPersonId: 'p-r4pl1', assignedPerson: { displayName: 'Kelly Thompson' }, scopeType: 'region_4' },
              { id: 'r4-pl2', title: 'Technical Lead', assignedPersonId: 'p-r4pl2', assignedPerson: { displayName: 'Marcus Rivera' }, scopeType: 'region_4' }
            ],
            staff: [
              { id: 'r4-ps1', title: 'Production Coordinator', assignedPersonId: 'p-r4ps1', assignedPerson: { displayName: 'Jasmine Liu' }, scopeType: 'region_4' },
              { id: 'r4-ps2', title: 'Logistics Coordinator', assignedPersonId: 'p-r4ps2', assignedPerson: { displayName: 'Tyler Adams' }, scopeType: 'region_4' }
            ]
          },
          experience: {
            manager: { id: 'r4-em', title: 'Experience Manager', assignedPersonId: 'p-r4em', assignedPerson: { displayName: 'Maria Santos' }, scopeType: 'region_4' },
            leads: [
              { id: 'r4-el1', title: 'Guest Experience Lead', assignedPersonId: 'p-r4el1', assignedPerson: { displayName: 'Derek Park' }, scopeType: 'region_4' }
            ],
            staff: [
              { id: 'r4-es1', title: 'Experience Staff', assignedPersonId: 'p-r4es1', assignedPerson: { displayName: 'Priya Sharma' }, scopeType: 'region_4' }
            ]
          }
        }
      },
      '5': {
        manager: {
          id: 'rom-5',
          title: 'Operations & Logistics Manager',
          assignedPersonId: 'p-rom5',
          assignedPerson: { displayName: 'Robert Garcia', email: 'robert.garcia@cognitive.com' },
          scopeType: 'region_5',
          levelLabel: 'Manager'
        },
        departments: {
          production: {
            manager: { id: 'r5-pm', title: 'Production Manager', assignedPersonId: 'p-r5pm', assignedPerson: { displayName: 'Michelle Wong' }, scopeType: 'region_5' },
            leads: [
              { id: 'r5-pl1', title: 'Production Lead', assignedPersonId: 'p-r5pl1', assignedPerson: { displayName: 'Daniel Brown' }, scopeType: 'region_5' }
            ],
            staff: [
              { id: 'r5-ps1', title: 'Production Staff', assignedPersonId: 'p-r5ps1', assignedPerson: { displayName: 'Emily Chen' }, scopeType: 'region_5' }
            ]
          },
          experience: {
            manager: { id: 'r5-em', title: 'Experience Manager', assignedPerson: null, scopeType: 'region_5' },
            leads: [],
            staff: [
              { id: 'r5-es1', title: 'Experience Coordinator', assignedPersonId: 'p-r5es1', assignedPerson: { displayName: 'Jason Park' }, scopeType: 'region_5' }
            ]
          }
        }
      }
    }
  },
  uniqueIP: {
    director: {
      id: 'ip-dir-ops',
      title: 'Director of Operations',
      assignedPersonId: 'p-ip-dir-ops',
      assignedPerson: { displayName: 'Angela Foster', email: 'angela.foster@soulfood.com' },
      scopeType: 'unique_ip',
      levelLabel: 'Director'
    },
    subDirectors: [
      { id: 'ip-sd-prod', title: 'Director of Production', assignedPersonId: 'p-ip-sd-prod', assignedPerson: { displayName: 'Marcus Johnson' }, scopeType: 'unique_ip', department: 'Production' },
      { id: 'ip-sd-exp', title: 'Director of Experience', assignedPersonId: 'p-ip-sd-exp', assignedPerson: { displayName: 'Sophia Martinez' }, scopeType: 'unique_ip', department: 'Experience' },
      { id: 'ip-sd-hosp', title: 'Director of Hospitality', assignedPersonId: 'p-ip-sd-hosp', assignedPerson: { displayName: 'Jordan Rivers' }, scopeType: 'unique_ip', department: 'Hospitality' }
    ],
    heads: [
      { id: 'ip-h1', title: 'Head of Production', assignedPersonId: 'p-ip-h1', assignedPerson: { displayName: 'Kevin Park' }, scopeType: 'unique_ip', department: 'Production' },
      { id: 'ip-h2', title: 'Head of Guest Services', assignedPersonId: 'p-ip-h2', assignedPerson: { displayName: 'Lisa Chen' }, scopeType: 'unique_ip', department: 'Experience' },
      { id: 'ip-h3', title: 'Head of Venue Ops', assignedPerson: null, scopeType: 'unique_ip', department: 'Operations' },
      { id: 'ip-h4', title: 'Head of Logistics', assignedPersonId: 'p-ip-h4', assignedPerson: { displayName: 'Tom Bradley' }, scopeType: 'unique_ip', department: 'Logistics' }
    ],
    leads: [
      { id: 'ip-l1', title: 'Production Lead', assignedPersonId: 'p-ip-l1', assignedPerson: { displayName: 'Maria Santos' }, scopeType: 'unique_ip' },
      { id: 'ip-l2', title: 'Experience Lead', assignedPersonId: 'p-ip-l2', assignedPerson: { displayName: 'Derek Williams' }, scopeType: 'unique_ip' },
      { id: 'ip-l3', title: 'Hospitality Lead', assignedPersonId: 'p-ip-l3', assignedPerson: { displayName: 'Nina Patel' }, scopeType: 'unique_ip' },
      { id: 'ip-l4', title: 'Site Operations Lead', assignedPerson: null, scopeType: 'unique_ip' }
    ],
    staff: [
      { id: 'ip-s1', title: 'Production Coordinator', assignedPersonId: 'p-ip-s1', assignedPerson: { displayName: 'Tyler Adams' }, scopeType: 'unique_ip' },
      { id: 'ip-s2', title: 'Guest Services Rep', assignedPersonId: 'p-ip-s2', assignedPerson: { displayName: 'Jasmine Liu' }, scopeType: 'unique_ip' },
      { id: 'ip-s3', title: 'Logistics Coordinator', assignedPersonId: 'p-ip-s3', assignedPerson: { displayName: 'Brandon Cole' }, scopeType: 'unique_ip' },
      { id: 'ip-s4', title: 'Venue Staff', assignedPersonId: 'p-ip-s4', assignedPerson: { displayName: 'Priya Sharma' }, scopeType: 'unique_ip' },
      { id: 'ip-s5', title: 'Operations Assistant', assignedPersonId: 'p-ip-s5', assignedPerson: { displayName: 'Ryan Murphy' }, scopeType: 'unique_ip' }
    ]
  }
};

// Role Node Component
const RoleNode = ({ role, style, onClick, size = 'normal' }) => {
  const isVacant = !role?.assignedPersonId;
  const nodeStyle = isVacant ? OPS_STYLES.vacant : style;
  const width = size === 'small' ? 'w-[110px]' : size === 'large' ? 'w-[170px]' : 'w-[140px]';
  
  return (
    <div
      className={`flex-shrink-0 cursor-pointer transition-all duration-200 hover:scale-105 hover:shadow-lg ${width}`}
      onClick={() => onClick?.(role)}
      data-testid={`ops-node-${role?.id}`}
    >
      <div
        className="rounded-lg shadow-md overflow-hidden"
        style={{ backgroundColor: nodeStyle.bg, border: `2px solid ${nodeStyle.border}` }}
      >
        <div className="px-2 py-1.5 text-center">
          <h3 className="font-semibold text-[9px] leading-tight" style={{ color: nodeStyle.text }}>
            {role?.title || 'Role'}
          </h3>
        </div>
        <div 
          className="px-2 py-1 text-center"
          style={{ backgroundColor: isVacant ? '#FFFBEB' : 'rgba(255,255,255,0.95)' }}
        >
          {isVacant ? (
            <span className="text-[9px] font-medium" style={{ color: '#92400E' }}>Vacant</span>
          ) : (
            <span className="text-[9px] font-medium" style={{ color: '#374151' }}>
              {role?.assignedPerson?.displayName || 'Assigned'}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Tier Header Component
const TierHeader = ({ label, color = '#6B7280', icon: Icon }) => (
  <div className="flex justify-center mb-3">
    <div className="flex items-center gap-2 px-4 py-1.5 rounded-full shadow-sm" style={{ backgroundColor: color }}>
      {Icon && <Icon className="w-3 h-3 text-white" />}
      <span className="text-xs font-semibold uppercase tracking-wide text-white">{label}</span>
    </div>
  </div>
);

// Connector Line Component
const ConnectorLine = ({ height = 'h-5' }) => (
  <div className="flex justify-center">
    <div className={`w-0.5 ${height}`} style={{ backgroundColor: '#CBD5E1' }} />
  </div>
);

// Horizontal Connector
const HorizontalConnector = () => (
  <div className="flex justify-center">
    <div className="w-full h-0.5 max-w-[90%]" style={{ backgroundColor: '#CBD5E1' }} />
  </div>
);

// Department Column Component
const DepartmentColumn = ({ title, data, color, onClick }) => {
  if (!data) return null;
  
  return (
    <div className="flex flex-col items-center gap-1.5 min-w-[120px]">
      <div className="text-[9px] font-medium px-2 py-0.5 rounded" style={{ backgroundColor: `${color}20`, color }}>
        {title}
      </div>
      {data.manager && (
        <RoleNode role={data.manager} style={OPS_STYLES.regional.deptManager} onClick={onClick} size="small" />
      )}
      {data.leads?.length > 0 && (
        <div className="flex flex-col items-center gap-1">
          {data.leads.map((lead, idx) => (
            <RoleNode key={lead.id || idx} role={lead} style={OPS_STYLES.regional.lead} onClick={onClick} size="small" />
          ))}
        </div>
      )}
      {data.staff?.length > 0 && (
        <div className="flex flex-col items-center gap-1">
          {data.staff.map((staff, idx) => (
            <RoleNode key={staff.id || idx} role={staff} style={OPS_STYLES.regional.staff} onClick={onClick} size="small" />
          ))}
        </div>
      )}
    </div>
  );
};

// Person Profile Sheet
const PersonProfileSheet = ({ open, onClose, role }) => {
  if (!role) return null;
  
  const person = role.assignedPerson;
  const isVacant = !role.assignedPersonId;
  
  // Demo social media stats
  const socialStats = {
    twitter: { followers: '8.1K', handle: '@' + (person?.displayName?.split(' ')[0]?.toLowerCase() || 'user') },
    linkedin: { connections: '750+' },
    instagram: { followers: '5.4K' }
  };

  // Demo skills based on department
  const getSkills = () => {
    const title = (role.title || '').toLowerCase();
    if (title.includes('production')) {
      return ['Production Management', 'Event Operations', 'Vendor Coordination', 'Technical Production', 'Stage Management'];
    } else if (title.includes('experience') || title.includes('guest')) {
      return ['Guest Services', 'Customer Experience', 'Event Management', 'Hospitality', 'Problem Resolution'];
    } else if (title.includes('logistics') || title.includes('site')) {
      return ['Logistics Planning', 'Site Management', 'Vendor Ops', 'Supply Chain', 'Inventory Control'];
    } else if (title.includes('hospitality')) {
      return ['Hospitality Management', 'VIP Services', 'Guest Relations', 'Service Excellence', 'Event Coordination'];
    }
    return ['Operations', 'Process Management', 'Team Leadership', 'Project Coordination', 'Vendor Relations'];
  };

  // Other roles (demo)
  const otherRoles = isVacant ? [] : [
    { scope: 'National', title: 'Operations Committee Member' },
    { scope: role.scopeType?.includes('region') ? `Region ${role.scopeType.split('_')[1]}` : 'Cross-functional', title: 'Safety Officer' }
  ];

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-[400px] sm:max-w-[400px] overflow-y-auto">
        <SheetHeader className="pb-4 border-b" style={{ borderColor: '#E9ECEF' }}>
          <SheetTitle className="flex items-center gap-3">
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{ backgroundColor: '#DBEAFE', color: '#1E40AF' }}
            >
              {isVacant ? (
                <User className="w-6 h-6" />
              ) : (
                <span className="text-lg font-semibold">
                  {person?.displayName?.split(' ').map(n => n[0]).join('').slice(0, 2) || 'U'}
                </span>
              )}
            </div>
            <div>
              <h2 className="text-lg font-semibold" style={{ color: '#264653' }}>
                {isVacant ? 'Vacant Position' : person?.displayName}
              </h2>
              <p className="text-sm font-normal" style={{ color: '#6B7280' }}>{role.title}</p>
            </div>
          </SheetTitle>
        </SheetHeader>

        <div className="py-5 space-y-5">
          {/* Scope Badge */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Scope</h4>
            <Badge 
              variant="outline" 
              className="px-3 py-1"
              style={{ 
                borderColor: role.scopeType === 'unique_ip' ? '#DC2626' : role.scopeType?.includes('region') ? '#059669' : '#1E40AF',
                color: role.scopeType === 'unique_ip' ? '#B91C1C' : role.scopeType?.includes('region') ? '#047857' : '#1E3A8A'
              }}
            >
              <Globe className="w-3 h-3 mr-1.5" />
              {role.scopeType === 'unique_ip' ? 'UNIQUE IP' : 
               role.scopeType?.includes('region') ? `Regional - ${role.scopeType.replace('region_', 'Region ')}` : 
               'National'}
            </Badge>
          </div>

          {/* Region Assignment */}
          {role.scopeType?.includes('region') && (
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Region Assignment</h4>
              <div className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: '#D1FAE5' }}>
                <MapPin className="w-4 h-4" style={{ color: '#059669' }} />
                <span className="text-sm font-medium" style={{ color: '#047857' }}>
                  Region {role.scopeType.replace('region_', '')}
                </span>
              </div>
            </div>
          )}

          {/* Contact Info */}
          {!isVacant && (
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Contact</h4>
              <div className="space-y-2">
                {person?.email && (
                  <div className="flex items-center gap-2 text-sm" style={{ color: '#374151' }}>
                    <Mail className="w-4 h-4" style={{ color: '#6B7280' }} />
                    <span>{person.email}</span>
                  </div>
                )}
                {person?.phone && (
                  <div className="flex items-center gap-2 text-sm" style={{ color: '#374151' }}>
                    <Phone className="w-4 h-4" style={{ color: '#6B7280' }} />
                    <span>{person.phone}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Other Roles Held */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Other Roles Held</h4>
            <div className="space-y-2">
              {isVacant ? (
                <p className="text-sm" style={{ color: '#9CA3AF' }}>No person assigned</p>
              ) : (
                otherRoles.map((r, idx) => (
                  <div key={idx} className="flex items-center gap-2 p-2 rounded-lg" style={{ backgroundColor: '#F3F4F6' }}>
                    <Briefcase className="w-4 h-4" style={{ color: '#6B7280' }} />
                    <div>
                      <span className="text-sm" style={{ color: '#374151' }}>{r.title}</span>
                      <span className="text-xs ml-2" style={{ color: '#9CA3AF' }}>({r.scope})</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Social Media Stats */}
          {!isVacant && (
            <div>
              <h4 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Social Influence</h4>
              <div className="grid grid-cols-3 gap-2">
                <div className="p-3 rounded-lg text-center" style={{ backgroundColor: '#E0F2FE' }}>
                  <Twitter className="w-4 h-4 mx-auto mb-1" style={{ color: '#0EA5E9' }} />
                  <p className="text-xs font-semibold" style={{ color: '#0369A1' }}>{socialStats.twitter.followers}</p>
                  <p className="text-[10px]" style={{ color: '#6B7280' }}>Followers</p>
                </div>
                <div className="p-3 rounded-lg text-center" style={{ backgroundColor: '#DBEAFE' }}>
                  <Linkedin className="w-4 h-4 mx-auto mb-1" style={{ color: '#1E40AF' }} />
                  <p className="text-xs font-semibold" style={{ color: '#1E3A8A' }}>{socialStats.linkedin.connections}</p>
                  <p className="text-[10px]" style={{ color: '#6B7280' }}>Connections</p>
                </div>
                <div className="p-3 rounded-lg text-center" style={{ backgroundColor: '#FCE7F3' }}>
                  <Instagram className="w-4 h-4 mx-auto mb-1" style={{ color: '#EC4899' }} />
                  <p className="text-xs font-semibold" style={{ color: '#BE185D' }}>{socialStats.instagram.followers}</p>
                  <p className="text-[10px]" style={{ color: '#6B7280' }}>Followers</p>
                </div>
              </div>
            </div>
          )}

          {/* Skills */}
          <div>
            <h4 className="text-xs font-medium uppercase tracking-wider mb-2" style={{ color: '#6B7280' }}>Skill Tags</h4>
            <div className="flex flex-wrap gap-2">
              {getSkills().map((skill, idx) => (
                <Badge 
                  key={idx}
                  variant="secondary"
                  className="text-xs"
                  style={{ backgroundColor: '#F3F4F6', color: '#374151' }}
                >
                  {skill}
                </Badge>
              ))}
            </div>
          </div>

          {/* Level Info */}
          <div className="p-4 rounded-lg" style={{ backgroundColor: '#F9FAFB', border: '1px solid #E9ECEF' }}>
            <div className="flex items-center justify-between">
              <span className="text-xs" style={{ color: '#6B7280' }}>Level</span>
              <span className="text-sm font-medium" style={{ color: '#264653' }}>
                {role.levelLabel || 'Staff'}
              </span>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs" style={{ color: '#6B7280' }}>Department</span>
              <span className="text-sm font-medium" style={{ color: '#264653' }}>
                {role.department || 'Operations'}
              </span>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Main Operations Department View Component
const OperationsDepartmentView = () => {
  const [hierarchy, setHierarchy] = useState(DEMO_OPS_HIERARCHY);
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [zoom, setZoom] = useState(0.75);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // In a real implementation, we would fetch and filter operational roles
        // For now, use demo data
        setHierarchy(DEMO_OPS_HIERARCHY);
      } catch (error) {
        console.error('Error fetching operations data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const handleNodeClick = (role) => {
    setSelectedRole(role);
    setProfileOpen(true);
  };

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.1, 1.2));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.4));
  const handleResetZoom = () => setZoom(0.75);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[700px] rounded-xl" style={{ backgroundColor: '#F9FAFB', border: '1px solid #E5E7EB' }}>
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-3" style={{ borderColor: '#3B82F6', borderTopColor: 'transparent' }} />
          <p style={{ color: '#6B7280' }}>Loading operations department view...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div 
        className="rounded-xl overflow-hidden relative" 
        style={{ height: '850px', backgroundColor: '#FAFBFC', border: '1px solid #E5E7EB' }}
        data-testid="operations-department-view"
      >
        {/* Header */}
        <div className="absolute top-4 left-4 right-4 z-10 flex items-center justify-between">
          {/* Zoom Controls */}
          <div className="flex gap-2 bg-white rounded-lg shadow-md p-2">
            <Button variant="ghost" size="sm" onClick={handleZoomIn} className="h-8 w-8 p-0" title="Zoom In">
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleZoomOut} className="h-8 w-8 p-0" title="Zoom Out">
              <ZoomOut className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={handleResetZoom} className="h-8 w-8 p-0" title="Reset Zoom">
              <Maximize2 className="w-4 h-4" />
            </Button>
            <div className="text-xs flex items-center px-2" style={{ color: '#6B7280' }}>{Math.round(zoom * 100)}%</div>
          </div>

          {/* Title */}
          <div className="bg-white rounded-lg shadow-md px-4 py-2">
            <div className="flex items-center gap-2">
              <Cog className="w-5 h-5" style={{ color: '#3B82F6' }} />
              <span className="font-semibold" style={{ color: '#264653' }}>Operations Department View</span>
              <Badge variant="outline" className="text-xs" style={{ borderColor: '#3B82F6', color: '#3B82F6' }}>
                Functional Matrix
              </Badge>
            </div>
          </div>

          {/* Legend */}
          <div className="bg-white rounded-lg shadow-md p-3">
            <div className="text-xs font-medium mb-2" style={{ color: '#374151' }}>Legend</div>
            <div className="flex gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#3B82F6' }} />
                <span className="text-[10px]" style={{ color: '#6B7280' }}>National</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#059669' }} />
                <span className="text-[10px]" style={{ color: '#6B7280' }}>Regional</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#DC2626' }} />
                <span className="text-[10px]" style={{ color: '#6B7280' }}>UNIQUE IP</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded" style={{ backgroundColor: '#FEF3C7', border: '1px solid #FDE68A' }} />
                <span className="text-[10px]" style={{ color: '#6B7280' }}>Vacant</span>
              </div>
            </div>
          </div>
        </div>

        {/* Chart Content */}
        <ScrollArea className="h-full w-full pt-20">
          <div 
            className="min-h-full py-8 px-4"
            style={{ transform: `scale(${zoom})`, transformOrigin: 'top center', minWidth: 'max-content' }}
          >
            {/* =============== NATIONAL TIER =============== */}
            <div className="mb-10">
              {/* TIER 1: COO */}
              {hierarchy.national.coo && (
                <>
                  <TierHeader label="Chief Operating Officer" color="#1E40AF" icon={Star} />
                  <div className="flex justify-center mb-3">
                    <RoleNode 
                      role={hierarchy.national.coo} 
                      style={OPS_STYLES.national.coo}
                      onClick={handleNodeClick}
                      size="large"
                    />
                  </div>
                  <ConnectorLine height="h-4" />
                </>
              )}

              {/* Director of Operations */}
              <TierHeader label="Director of Operations" color="#3B82F6" icon={Settings2} />
              <div className="flex justify-center mb-3">
                <RoleNode 
                  role={hierarchy.national.director} 
                  style={OPS_STYLES.national.director}
                  onClick={handleNodeClick}
                  size="large"
                />
              </div>
              <ConnectorLine height="h-4" />

              {/* TIER 2: National Sub-Directors */}
              <TierHeader label="National Functional Directors" color="#60A5FA" icon={Building2} />
              <div className="flex justify-center gap-4 mb-4">
                {hierarchy.national.subDirectors.map((dir, idx) => (
                  <RoleNode 
                    key={dir.id || idx}
                    role={dir} 
                    style={OPS_STYLES.national.subDirector}
                    onClick={handleNodeClick}
                  />
                ))}
              </div>
              <ConnectorLine height="h-6" />

              {/* TIER 3: Regional Operations Managers */}
              <TierHeader label="Regional Operations & Logistics Managers" color="#059669" icon={Truck} />
              <div className="flex justify-center gap-5 mb-4">
                {[1, 2, 3, 4, 5].map(regionNum => (
                  <div key={regionNum} className="flex flex-col items-center">
                    <div className="text-[10px] font-medium mb-2 px-2 py-0.5 rounded" style={{ backgroundColor: '#D1FAE5', color: '#059669' }}>
                      Region {regionNum}
                    </div>
                    <RoleNode 
                      role={hierarchy.national.regions[regionNum]?.manager} 
                      style={OPS_STYLES.regional.manager}
                      onClick={handleNodeClick}
                    />
                  </div>
                ))}
              </div>
              <ConnectorLine height="h-4" />

              {/* TIER 4: Regional Department Branches */}
              <TierHeader label="Regional Department Structure" color="#10B981" icon={Package} />
              <div className="flex justify-center gap-6">
                {[1, 2, 3, 4, 5].map(regionNum => (
                  <div key={regionNum} className="flex flex-col items-center">
                    <div className="text-[9px] font-semibold mb-2 px-3 py-1 rounded-full" style={{ backgroundColor: '#D1FAE5', color: '#047857' }}>
                      R{regionNum}
                    </div>
                    <div className="flex gap-3">
                      <DepartmentColumn 
                        title="Production" 
                        data={hierarchy.national.regions[regionNum]?.departments?.production}
                        color="#8B5CF6"
                        onClick={handleNodeClick}
                      />
                      <DepartmentColumn 
                        title="Experience" 
                        data={hierarchy.national.regions[regionNum]?.departments?.experience}
                        color="#EC4899"
                        onClick={handleNodeClick}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* =============== UNIQUE IP TIER =============== */}
            <div 
              className="mt-10 pt-8 border-t-2"
              style={{ borderColor: '#DC2626', borderStyle: 'dashed' }}
            >
              <div className="flex justify-center mb-4">
                <div className="px-4 py-2 rounded-lg" style={{ backgroundColor: '#FEE2E2', border: '2px solid #DC2626' }}>
                  <span className="font-bold text-sm uppercase tracking-wider" style={{ color: '#B91C1C' }}>
                    UNIQUE IP Operations Branch
                  </span>
                </div>
              </div>

              {/* UNIQUE IP Director */}
              <TierHeader label="Director of Operations" color="#DC2626" icon={Settings2} />
              <div className="flex justify-center mb-3">
                <RoleNode 
                  role={hierarchy.uniqueIP.director} 
                  style={OPS_STYLES.uniqueIP.director}
                  onClick={handleNodeClick}
                  size="large"
                />
              </div>
              <ConnectorLine />

              {/* UNIQUE IP Sub-Directors */}
              <TierHeader label="Functional Directors" color="#EF4444" icon={Building2} />
              <div className="flex justify-center gap-4 mb-3">
                {hierarchy.uniqueIP.subDirectors.map((dir, idx) => (
                  <RoleNode 
                    key={dir.id || idx}
                    role={dir} 
                    style={OPS_STYLES.uniqueIP.subDirector}
                    onClick={handleNodeClick}
                  />
                ))}
              </div>
              <ConnectorLine />

              {/* UNIQUE IP Heads */}
              <TierHeader label="Department Heads" color="#F87171" icon={Users} />
              <div className="flex justify-center gap-3 mb-3 flex-wrap">
                {hierarchy.uniqueIP.heads.map((head, idx) => (
                  <RoleNode 
                    key={head.id || idx}
                    role={head} 
                    style={OPS_STYLES.uniqueIP.head}
                    onClick={handleNodeClick}
                    size="small"
                  />
                ))}
              </div>
              <ConnectorLine />

              {/* UNIQUE IP Leads */}
              <TierHeader label="Team Leads" color="#FCA5A5" />
              <div className="flex justify-center gap-3 mb-3 flex-wrap">
                {hierarchy.uniqueIP.leads.map((lead, idx) => (
                  <RoleNode 
                    key={lead.id || idx}
                    role={lead} 
                    style={OPS_STYLES.uniqueIP.lead}
                    onClick={handleNodeClick}
                    size="small"
                  />
                ))}
              </div>
              <ConnectorLine />

              {/* UNIQUE IP Staff */}
              <TierHeader label="Team Members" color="#FEE2E2" />
              <div className="flex justify-center gap-3 flex-wrap">
                {hierarchy.uniqueIP.staff.map((staff, idx) => (
                  <RoleNode 
                    key={staff.id || idx}
                    role={staff} 
                    style={OPS_STYLES.uniqueIP.staff}
                    onClick={handleNodeClick}
                    size="small"
                  />
                ))}
              </div>
            </div>
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>

      {/* Person Profile Sheet */}
      <PersonProfileSheet 
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        role={selectedRole}
      />
    </>
  );
};

export default OperationsDepartmentView;
